import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Code,
  Flex,
  FormLabel,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { utils as commonUtils } from '@finley-technologies/common';
import { getPartyShell } from '@finley-technologies/credit-agreement-utils';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import {
  CheckboxFormField,
  FormFieldLabel,
  InputFormField,
} from 'packages/common-frontend/components/FormField';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const allCustomerTypeOptions = [
  {
    value: CreditAgreement.DomainEntity.Items.CustomerType.Owner,
    label: 'Owner: full permissions',
  },
  {
    value: CreditAgreement.DomainEntity.Items.CustomerType.Limited,
    label: 'Limited: some permissions',
  },
  {
    value: CreditAgreement.DomainEntity.Items.CustomerType.None,
    label: 'None: login disallowed',
  },
];

const allowedCustomerTypes: Partial<
  Record<
    CreditAgreement.DomainEntity.Items.PartyType,
    { label: string; value: CreditAgreement.DomainEntity.Items.CustomerType }[]
  >
> = {
  // Only borrowers and capital providers are ever given logins.
  // This is enforced by the PartyEntity type definition and corresponding generated jsonschema.
  borrower: allCustomerTypeOptions,
  capitalProvider: allCustomerTypeOptions,
};

type EditPartyModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  partyKey: CreditAgreement.DomainEntity.Items.PartyType;
};
export const EditPartyModal = ({
  isOpen,
  setIsOpen,
  wipConfig: {
    formProps,
    organizationMap,
    arePartiesLocked,
    setCapitalProvidersMap,
    capitalProvidersMap,
    saveCreditAgreementFullArtifactWip,
  },
  basePropertyKey,
  partyKey,
}: EditPartyModalProps) => {
  const selectOptions = _.sortBy(
    _.map(_.values(organizationMap), (org) => ({
      value: org.entity.id,
      label: commonUtils.toTitleCase(org.entity.slug),
    })),
    'label',
  );

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="2xl">
      <ModalOverlay />
      <ModalContent padding="20px" minW="3xl">
        <ModalHeader>Edit Party</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!arePartiesLocked && (
            <Flex alignItems="flex-end">
              <FormLabel w="60">Party</FormLabel>
              <InputGroup width="100%">
                <Select
                  value={getIn(
                    formProps.values,
                    `${basePropertyKey}.entity.id`,
                  )}
                  onChange={(val) => {
                    const currentOrgEntity = getIn(
                      formProps.values,
                      basePropertyKey,
                    );
                    const properties = _.omit(
                      currentOrgEntity,
                      'entity',
                      'users',
                      'bankAccounts',
                    );
                    const orgEntity = organizationMap[val];
                    const partyShell = getPartyShell({
                      type: partyKey,
                      organizationId: uuidv4(),
                    });

                    formProps.setFieldValue(basePropertyKey, {
                      ...partyShell,
                      ...orgEntity,
                      ...properties,
                    });

                    if (
                      partyKey ===
                      CreditAgreement.DomainEntity.Items.PartyType
                        .CapitalProvider
                    ) {
                      const omittedCapitalProviderMap = _.omit(
                        capitalProvidersMap,
                        currentOrgEntity.entity.id,
                      );
                      setCapitalProvidersMap({
                        ...omittedCapitalProviderMap,
                        [orgEntity.entity.id]: orgEntity.entity,
                      });
                    }
                  }}
                  options={selectOptions}
                  width="sm"
                />
              </InputGroup>
            </Flex>
          )}
          {allowedCustomerTypes[partyKey] && (
            <>
              <Alert status="info" mb={5}>
                <AlertIcon />
                <AlertDescription>
                  <Text my={3}>
                    Typically for a non-verification facility, the borrower is{' '}
                    <Code>Owner</Code>. Capital provider is <Code>None</Code>,
                    or optionally <Code>Limited</Code> to allow login, e.g. for
                    Insights.
                  </Text>
                  <Text my={3}>
                    Typically for a verification facility, the capital provider
                    is <Code>Owner</Code>. Borrower is <Code>None</Code>, or
                    optionally <Code>Limited</Code> to allow login, e.g. for
                    borrower remediation.
                  </Text>
                </AlertDescription>
              </Alert>
              <Flex alignItems="flex-end">
                <FormFieldLabel label="Customer Type" labelWidth="60" />
                <InputGroup width="100%">
                  <Select
                    value={getIn(
                      formProps.values,
                      `${basePropertyKey}.customerType`,
                    )}
                    onChange={(newValue) => {
                      formProps.setFieldValue(
                        `${basePropertyKey}.customerType`,
                        newValue,
                      );
                    }}
                    options={allowedCustomerTypes[partyKey]}
                  />
                </InputGroup>
              </Flex>
            </>
          )}
          <InputFormField
            propName={`${basePropertyKey}.section`}
            label="Section"
            formProps={formProps}
            value={getIn(formProps.values, `${basePropertyKey}.section`)}
            labelWidth="60"
            inputWidth="sm"
            flexDirection="row"
            alignItems="flex-end"
            marginTop={5}
          />
          <CheckboxFormField
            propName={`${basePropertyKey}._isWrittenIn_`}
            label="Is Written In"
            formProps={formProps}
            value={getIn(formProps.values, `${basePropertyKey}._isWrittenIn_`)}
            labelWidth="60"
            inputWidth="sm"
            flexDirection="row"
            marginTop={5}
          />

          <Flex justifyContent="center" mt="8">
            <Button
              onClick={() => {
                saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: formProps.values.id,
                      creditFacilityId: formProps.values.creditFacilityId,
                      creditAgreement: JSON.stringify(formProps.values),
                    },
                  },
                });
                setIsOpen(false);
              }}
              aria-label="Done"
              isDisabled={_.keys(formProps.errors).length > 0}
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
